import React, { useState, useEffect } from 'react';
import { io } from 'socket.io-client';
import AdminPane from '../../AdminPane';
import { PrismBanner, PrismSwitch, PrismSelect, PrismLoading, PrismInputTextarea, PrismButton } from '@prism-ui/react';
import { toast } from '../../../Toaster/toast';
import axios from 'axios';
import EmailHealthWidget from './EmailHealthWidget';
import type { EmailHealthResponse } from '../../../../types/emailHealth';
import { buildProjectFairYearOptions } from './siteSettings.utils';
import {
  ESSAY_AI_PROMPT_DEFAULT,
  ESSAY_AI_PROMPT_ENABLED_KEY,
  ESSAY_AI_PROMPT_MARKDOWN_KEY,
  readEssayAiPrompt,
  upsertSetting,
} from '../../../../utils/essayAiPrompt';
import './SiteSettings.css';

export const SiteSettings: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [officerLoginAsEnabled, setOfficerLoginAsEnabled] = useState(false);
  const [darkModeMinLevel, setDarkModeMinLevel] = useState(0);
  const [savingDarkMode, setSavingDarkMode] = useState(false);
  const [projectFairNavYear, setProjectFairNavYear] = useState('2025');
  const [projectFairYears, setProjectFairYears] = useState<Array<{ value: string; display: string }>>([]);
  const [savingProjectFairYear, setSavingProjectFairYear] = useState(false);
  const [roles, setRoles] = useState<{ id: number; name: string; permission_level: number }[]>([]);

  // The AI coaching prompt offered on the /apply essay step. Surfacing the markdown here
  // means the wording can be tuned between cohorts without a deploy, and every change
  // lands in the audit log because the generic settings endpoints log create and update.
  const [aiPromptEnabled, setAiPromptEnabled] = useState(true);
  const [aiPromptMarkdown, setAiPromptMarkdown] = useState(ESSAY_AI_PROMPT_DEFAULT);
  const [aiPromptDraft, setAiPromptDraft] = useState(ESSAY_AI_PROMPT_DEFAULT);
  const [savingAiPrompt, setSavingAiPrompt] = useState(false);

  // Email health state
  const [emailHealth, setEmailHealth] = useState<EmailHealthResponse | null>(null);
  const [emailHealthLoading, setEmailHealthLoading] = useState(false);
  const [emailHealthError, setEmailHealthError] = useState<string | null>(null);
  const [healthExpanded, setHealthExpanded] = useState(false);

  useEffect(() => {
    fetchSettings();
    axios.get('/api/roles').then(r => {
      const sorted = [...r.data].sort((a, b) => a.permission_level - b.permission_level);
      setRoles(sorted);
    }).catch(() => {});

    // Initial email health fetch
    fetchEmailHealth();

    // Subscribe to socket updates — no polling needed after initial load
    const socket = io({ path: '/socket.io/', transports: ['websocket', 'polling'], withCredentials: true });
    socket.on('email-health:updated', (data: EmailHealthResponse) => {
      setEmailHealth(data);
      setEmailHealthLoading(false);
      setEmailHealthError(null);
    });
    return () => {
      socket.off('email-health:updated');
      socket.disconnect();
    };
  }, []);

  const fetchEmailHealth = async () => {
    try {
      setEmailHealthLoading(true);
      setEmailHealthError(null);
      const response = await axios.get<EmailHealthResponse>('/api/email/health');
      setEmailHealth(response.data);
    } catch (err: any) {
      setEmailHealthError(err?.response?.data?.error ?? err?.message ?? 'Unknown error');
    } finally {
      setEmailHealthLoading(false);
    }
  };

  const fetchSettings = async () => {
    try {
      setLoading(true);
      const [settingsResponse, yearsResponse] = await Promise.all([
        axios.get('/api/adminSettings/settings'),
        axios.get('/api/project_fair/years'),
      ]);
      const settings = settingsResponse.data;
      
      // Find the officer_login_as_enabled setting
      const loginAsSetting = settings.find((s: { setting_key: string }) => s.setting_key === 'officer_login_as_enabled');
      setOfficerLoginAsEnabled(loginAsSetting?.setting_value === 'true');
      const darkModeSetting = settings.find((s: { setting_key: string }) => s.setting_key === 'dark_mode_min_level');
      setDarkModeMinLevel(parseInt(darkModeSetting?.setting_value) || 0);
      const projectFairYearSetting = settings.find(
        (s: { setting_key: string }) => s.setting_key === 'project_fair_nav_year'
      );
      const configuredYear = String(projectFairYearSetting?.setting_value || '2025');
      setProjectFairNavYear(configuredYear);
      setProjectFairYears(buildProjectFairYearOptions([...yearsResponse.data, configuredYear]));

      const aiPrompt = readEssayAiPrompt(settings);
      setAiPromptEnabled(aiPrompt.enabled);
      setAiPromptMarkdown(aiPrompt.markdown);
      setAiPromptDraft(aiPrompt.markdown);
    } catch (error) {
      console.error('Error fetching settings:', error);
      toast.error('Failed to load site settings. Please refresh the page and try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleProjectFairYearChange = async (year: string) => {
    const previousYear = projectFairNavYear;
    try {
      setSavingProjectFairYear(true);
      setProjectFairNavYear(year);
      try {
        await axios.put('/api/adminSettings/settings/project_fair_nav_year', {
          setting_value: year,
        });
      } catch (error: any) {
        if (error.response?.status === 404) {
          await axios.post('/api/adminSettings/settings', {
            setting_key: 'project_fair_nav_year',
            setting_value: year,
            setting_type: 'number',
            description: 'Project Fairs year shown in the primary navigation menu',
            category: 'navigation',
          });
        } else {
          throw error;
        }
      }
      window.dispatchEvent(new CustomEvent('siteSettingsUpdated', {
        detail: { projectFairNavYear: year },
      }));
      toast.success(`The primary navigation now links to Project Fairs ${year}.`);
    } catch (error) {
      console.error('Error saving Project Fair navigation year:', error);
      toast.error('Failed to save the Project Fair navigation year. The change has been reverted — please try again.');
      setProjectFairNavYear(previousYear);
    } finally {
      setSavingProjectFairYear(false);
    }
  };

  const handleToggleChange = async (checked: boolean) => {
    try {
      setSaving(true);
      setOfficerLoginAsEnabled(checked);
      
      // Try to update first, if it doesn't exist, create it
      try {
        await axios.put('/api/adminSettings/settings/officer_login_as_enabled', {
          setting_value: checked ? 'true' : 'false',
        });
      } catch (error: any) {
        // If setting doesn't exist (404), create it
        if (error.response?.status === 404) {
          await axios.post('/api/adminSettings/settings', {
            setting_key: 'officer_login_as_enabled',
            setting_value: checked ? 'true' : 'false',
            setting_type: 'boolean',
            description: 'Allow officers to login as another user',
            category: 'user_management',
          });
        } else {
          throw error;
        }
      }

      toast.success(`User Impersonation is now ${checked ? 'enabled — Officers can impersonate any user via Manage Users' : 'disabled — Officers can no longer impersonate other users'}.`);
    } catch (error) {
      console.error('Error saving setting:', error);
      toast.error('Failed to save the User Impersonation setting. The change has been reverted — please try again.');
      // Revert the toggle on error
      setOfficerLoginAsEnabled(!checked);
    } finally {
      setSaving(false);
    }
  };

  const handleDarkModeLevelChange = async (level: number) => {
    const previousLevel = darkModeMinLevel;
    try {
      setSavingDarkMode(true);
      setDarkModeMinLevel(level);
      try {
        await axios.put('/api/adminSettings/settings/dark_mode_min_level', {
          setting_value: level.toString(),
        });
      } catch (error: any) {
        if (error.response?.status === 404) {
          await axios.post('/api/adminSettings/settings', {
            setting_key: 'dark_mode_min_level',
            setting_value: level.toString(),
            setting_type: 'number',
            description: 'Minimum permission level required to use dark mode',
            category: 'interface',
          });
        } else {
          throw error;
        }
      }
      const levelLabel = selectOptions.find(o => o.value === String(level))?.display ?? String(level);
      toast.success(`Dark Mode is now available to: ${levelLabel}. Users below this level will no longer see the Dark Mode toggle.`);
    } catch (error) {
      console.error('Error saving dark mode setting:', error);
      toast.error('Failed to save the Dark Mode availability setting. The change has been reverted — please try again.');
      setDarkModeMinLevel(previousLevel);
    } finally {
      setSavingDarkMode(false);
    }
  };

  const handleAiPromptEnabledChange = async (checked: boolean) => {
    const previous = aiPromptEnabled;
    try {
      setSavingAiPrompt(true);
      setAiPromptEnabled(checked);
      await upsertSetting({
        key: ESSAY_AI_PROMPT_ENABLED_KEY,
        value: checked ? 'true' : 'false',
        type: 'boolean',
        description: 'Show the AI coaching prompt on the application essay step',
        category: 'application',
      });
      toast.success(
        checked
          ? 'Applicants can now copy the AI coaching prompt from the essay step.'
          : 'The AI coaching prompt is now hidden on the essay step.'
      );
    } catch (error) {
      console.error('Error saving the essay AI prompt visibility:', error);
      toast.error('Failed to save the AI coaching prompt setting. The change has been reverted — please try again.');
      setAiPromptEnabled(previous);
    } finally {
      setSavingAiPrompt(false);
    }
  };

  const handleAiPromptSave = async () => {
    const next = aiPromptDraft.trim();
    if (!next) {
      toast.error('The AI coaching prompt cannot be empty.');
      return;
    }
    if (next === aiPromptMarkdown) return;

    const previous = aiPromptMarkdown;
    try {
      setSavingAiPrompt(true);
      setAiPromptMarkdown(next);
      await upsertSetting({
        key: ESSAY_AI_PROMPT_MARKDOWN_KEY,
        value: next,
        type: 'string',
        description: 'Markdown of the AI coaching prompt on the application essay step',
        category: 'application',
      });
      toast.success('The AI coaching prompt has been updated.');
    } catch (error) {
      console.error('Error saving the essay AI prompt:', error);
      toast.error('Failed to save the AI coaching prompt. The change has been reverted — please try again.');
      setAiPromptMarkdown(previous);
      setAiPromptDraft(previous);
    } finally {
      setSavingAiPrompt(false);
    }
  };

  const handleAiPromptReset = () => {
    setAiPromptDraft(ESSAY_AI_PROMPT_DEFAULT);
  };

  const maxPermissionLevel = roles.length > 0 ? Math.max(...roles.map(r => r.permission_level)) : null;  const selectOptions = [
    { value: '0', display: 'Everyone' },
    ...roles.map(role => ({
      value: String(role.permission_level),
      display: role.permission_level === maxPermissionLevel
        ? role.name.charAt(0).toUpperCase() + role.name.slice(1)
        : role.name.charAt(0).toUpperCase() + role.name.slice(1) + '+',
    })),
  ];

  return (
    <AdminPane width="narrow" className="site-settings-container">
      <div className="site-settings-header">
        <PrismBanner tone="info" layout="inline">
          <strong>Site Settings:</strong>
          <ul style={{ paddingLeft: '1.5rem', listStyleType: 'disc', marginBottom: 0, marginTop: '0.5rem' }}>
            <li>Controls global application behaviour — all changes take effect immediately for all users</li>
            <li><strong>Impersonation:</strong> when enabled, Officers can act as any user in the system via Manage Users. Disable when not actively needed to reduce risk.</li>
            <li><strong>Appearance:</strong> restricts which roles see the Dark Mode toggle in their personalized menu</li>
            <li><strong>Project Fairs Nav Menu:</strong> selects which Project Fairs year appears in the primary navigation</li>
            <li><strong>Essay AI Coaching Prompt:</strong> controls whether applicants see the "Copy prompt &amp; open Copilot" aid on the essay step, and the exact markdown prompt they receive</li>
            <li><strong>Email Health:</strong> live status of the outbound email subsystem; shows SMTP connectivity and recent send/failure counts</li>
            <li>Only Admins can modify these settings</li>
          </ul>
        </PrismBanner>
      </div>

      {loading ? (
        <div className="site-settings-loading">
          <PrismLoading as="spinner" container="inline" />
        </div>
      ) : (
        <div className="site-settings-content">
          <div className="settings-section">
            <h5>👻 Impersonation</h5>
            <div className="settings-row">
              <div className="settings-label">
                <strong>User Impersonation</strong>
                <p className="settings-hint">When enabled, Officers can impersonate any user via the Manage Users page. Disable this when not actively in use to limit impersonation exposure.</p>
              </div>
              <PrismSwitch
                label="User Impersonation"
                hideLabel
                checked={officerLoginAsEnabled}
                disabled={saving}
                onPrismChange={(e: CustomEvent<{ checked: boolean }>) => handleToggleChange(e.detail.checked)}
              />
            </div>
          </div>

          <div className="settings-section">
            <h5>🧪 Project Fairs Nav Menu</h5>
            <div className="settings-row">
              <div className="settings-label">
                <strong>Displayed Project Fairs Year</strong>
                <p className="settings-hint">
                  Controls the Project Fairs label and destination in the primary navigation menu. Existing fair
                  years and the current calendar year are available.
                </p>
              </div>
              <PrismSelect
                label="Displayed Project Fairs Year"
                hideLabel
                required
                name="project-fair-nav-year"
                value={projectFairNavYear}
                disabled={savingProjectFairYear}
                options={projectFairYears}
                style={{ width: '160px' }}
                onPrismChange={(event: CustomEvent<{ value?: string }>) => {
                  const year = event.detail?.value;
                  if (year) handleProjectFairYearChange(year);
                }}
              />
            </div>
          </div>

          <div className="settings-section">
            <h5>🪞 Appearance</h5>
            <div className="settings-row">
              <div className="settings-label">
                <strong>Dark Mode Available To</strong>
                <p className="settings-hint">Controls which user classes see the Dark Mode toggle in their menu</p>
              </div>
              <PrismSelect
                label="Dark Mode Available To"
                hideLabel
                name="dark-mode-min-level"
                value={String(darkModeMinLevel)}
                disabled={savingDarkMode}
                options={selectOptions}
                style={{ width: '160px' }}
                onPrismChange={(e: CustomEvent<any>) => {
                  const val = e.detail?.value ?? e.detail;
                  handleDarkModeLevelChange(parseInt(String(val), 10));
                }}
              />
            </div>
          </div>

          <div className="settings-section">
            <h5>🤖 Essay AI Coaching Prompt</h5>
            <div className="settings-row">
              <div className="settings-label">
                <strong>Show the AI Coaching Prompt</strong>
                <p className="settings-hint">
                  When enabled, the essay step offers applicants a "Copy prompt &amp; open Copilot"
                  button. The prompt interviews them about their own experience and is written to
                  refuse to draft the statement. Changes are recorded in the audit log.
                </p>
              </div>
              <PrismSwitch
                label="Show the AI Coaching Prompt"
                hideLabel
                checked={aiPromptEnabled}
                disabled={savingAiPrompt}
                onPrismChange={(e: CustomEvent<{ checked: boolean }>) => handleAiPromptEnabledChange(e.detail.checked)}
              />
            </div>
            <div className="settings-row settings-row--stacked">
              <div className="settings-label">
                <strong>Prompt Markdown</strong>
                <p className="settings-hint">
                  Markdown handed to the applicant's AI assistant. Keep it question-led — if it
                  starts producing prose for them, the statements stop telling us anything.
                </p>
              </div>
              <div className="settings-genai-editor">
                <PrismInputTextarea
                  label="Prompt markdown"
                  hideLabel
                  name="essay-ai-prompt-markdown"
                  rows={16}
                  value={aiPromptDraft}
                  disabled={savingAiPrompt || !aiPromptEnabled}
                  onPrismInput={(e: CustomEvent<{ value?: string }>) =>
                    setAiPromptDraft(e.detail?.value ?? '')
                  }
                />
                <div className="settings-genai-editor__actions">
                  <PrismButton
                    tone="primary"
                    size="sm"
                    loading={savingAiPrompt}
                    disabled={savingAiPrompt || !aiPromptEnabled || aiPromptDraft.trim() === aiPromptMarkdown}
                    onPrismClick={handleAiPromptSave}
                  >
                    <span>Save Prompt</span>
                  </PrismButton>
                  <PrismButton
                    display="outline"
                    tone="secondary"
                    size="sm"
                    disabled={savingAiPrompt || !aiPromptEnabled || aiPromptDraft === ESSAY_AI_PROMPT_DEFAULT}
                    onPrismClick={handleAiPromptReset}
                  >
                    <span>Restore Default</span>
                  </PrismButton>
                </div>
              </div>
            </div>
          </div>

          <div className="settings-section">
            <h5>📧 Email Health</h5>
            <EmailHealthWidget
              health={emailHealth}
              loading={emailHealthLoading}
              error={emailHealthError}
              expanded={healthExpanded}
              onToggleExpand={() => setHealthExpanded(e => !e)}
              onRefresh={fetchEmailHealth}
            />
          </div>
        </div>
      )}
    </AdminPane>
  );
};
